import { useScrollReveal } from '../hooks/useScrollReveal';
import { useParallax } from '../hooks/useParallax';

const modalities = [
  {
    label: '文本',
    sub: '内容与理论',
    icon: 'ri-file-text-line',
    color: 'primary',
  },
  {
    label: '声音',
    sub: '节奏与表达',
    icon: 'ri-volume-up-line',
    color: 'accent',
  },
  {
    label: '画面',
    sub: '活动与呈现',
    icon: 'ri-image-line',
    color: 'secondary',
  },
];

const outputs = [
  { label: '内容一致性', desc: '三种模态对同一教学目标的支撑是否统一', icon: 'ri-check-double-line' },
  { label: '表达感染力', desc: '声音节奏与画面呈现是否增强理论传达', icon: 'ri-emotion-line' },
  { label: '课堂沉浸度', desc: '多感官协同是否形成完整的课堂体验', icon: 'ri-focus-3-line' },
];

export default function P30MultiModalNew({ forceVisible = false }: { forceVisible?: boolean }) {
  const { ref: titleRef, isVisible: titleVisible } = useScrollReveal(0.12, forceVisible);
  const { ref: orb1Ref, offset: orb1Y } = useParallax(0.05);
  const { ref: orb2Ref, offset: orb2Y } = useParallax(-0.04);

  return (
    <div className="relative min-h-screen flex flex-col justify-start overflow-y-auto">
      {/* 三层背景光效 */}
      <div ref={orb1Ref} className="absolute top-20 right-24 w-[380px] h-[380px] rounded-full bg-primary-200/30 blur-[120px] pointer-events-none" style={{ transform: `translateY(${orb1Y}px)` }} />
      <div ref={orb2Ref} className="absolute bottom-28 left-12 w-[400px] h-[400px] rounded-full bg-accent-200/25 blur-[110px] pointer-events-none" style={{ transform: `translateY(${orb2Y}px)` }} />
      <div className="absolute top-1/2 left-1/3 w-[300px] h-[300px] rounded-full bg-secondary-200/20 blur-[140px] pointer-events-none animate-orb-float-3" />

      <div className="relative z-10 w-full max-w-[1400px] mx-auto px-6 md:px-10 lg:px-16 py-6 md:py-10">
        {/* 标题区 */}
        <div ref={titleRef} className="text-center mb-6 md:mb-8">
          <div className="flex items-center justify-center gap-2 mb-2">
            <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-primary-100/60 text-primary-700 text-base font-body font-semibold">
              <i className="ri-scan-line text-sm" /> 技术点四
            </span>
          </div>
          <h2 className={`font-heading text-4xl md:text-5xl lg:text-6xl font-bold text-foreground-950 transition-all duration-1000 delay-100 ${titleVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}>
            多模态互证与<span className="bg-gradient-to-r from-primary-500 to-accent-500 bg-clip-text text-transparent">课堂感染力</span>
          </h2>
          <p className={`mt-3 text-lg md:text-xl text-foreground-950 font-body max-w-2xl mx-auto transition-all duration-1000 delay-200 ${titleVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-6'}`}>
            将文本、声音、画面三类课堂证据汇聚融合，通过交叉验证还原真实的课堂感染力
          </p>
        </div>

        {/* ===== 互证核心 — 主内容区 ===== */}
        <div className={`max-w-5xl mx-auto mb-4 md:mb-5 transition-all duration-1000 delay-400 ${titleVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-12'}`}>
          <div className="p-6 md:p-8 rounded-2xl glass-liquid-gold border-accent-200/30 text-center">
            <h3 className="text-3xl md:text-4xl font-heading font-bold text-foreground-900">多模态互证引擎</h3>
            <p className="text-lg md:text-xl text-foreground-950 font-body leading-relaxed mt-3 max-w-2xl mx-auto">
              交叉验证三种模态的证据一致性，识别单一模态无法捕捉的课堂特征
            </p>
          </div>
        </div>

        {/* ===== 输出层 — 主内容 ===== */}
        <div className={`max-w-5xl mx-auto mb-4 md:mb-5 transition-all duration-1000 delay-500 ${titleVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 md:gap-6">
            {outputs.map((out, i) => (
              <div
                key={out.label}
                className="flex items-start gap-3 p-5 md:p-6 rounded-xl glass-liquid transition-all duration-500 hover:border-primary-300/40 hover:shadow-md"
                style={{ transitionDelay: `${500 + i * 100}ms` }}
              >
                <div className="w-13 h-13 flex items-center justify-center rounded-lg glass-gold border-accent-200/40 flex-shrink-0">
                  <i className={`${out.icon} text-2xl text-accent-600`} />
                </div>
                <div>
                  <h4 className="text-2xl md:text-3xl font-body font-bold text-foreground-950">{out.label}</h4>
                  <p className="text-lg md:text-xl text-foreground-950 font-body leading-relaxed mt-1">{out.desc}</p>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* ===== 汇聚箭头 ===== */}
        <div className={`flex justify-center mb-6 md:mb-8 transition-all duration-1000 delay-700 ${titleVisible ? 'opacity-100' : 'opacity-0'}`}>
          <div className="flex flex-col items-center">
            <div className="w-px h-4 bg-primary-300/60" />
            <div className="w-8 h-8 flex items-center justify-center rounded-full bg-primary-500">
              <i className="ri-arrow-down-line text-background-50 text-sm" />
            </div>
            <div className="w-px h-4 bg-primary-300/60" />
          </div>
        </div>

        {/* ===== 三模态输入层（精简版） ===== */}
        <div className={`max-w-5xl mx-auto transition-all duration-1000 delay-800 ${titleVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-12'}`}>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-2 md:gap-3">
            {modalities.map((m, i) => (
              <div
                key={m.label}
                className="group relative p-3 md:p-4 rounded-xl glass-liquid transition-all duration-500 hover:shadow-lg hover:-translate-y-1 text-center"
                style={{ transitionDelay: `${800 + i * 120}ms` }}
              >
                <div
                  className={`w-10 h-10 mx-auto mb-2 flex items-center justify-center rounded-lg flex-shrink-0 transition-transform duration-500 group-hover:scale-110 ${
                    m.color === 'primary' ? 'bg-primary-500' : m.color === 'accent' ? 'bg-accent-500' : 'bg-secondary-500'
                  }`}
                >
                  <i className={`${m.icon} text-lg text-background-50`} />
                </div>
                <h3 className="text-lg md:text-xl font-body font-bold text-foreground-950">{m.label}</h3>
                <p className="text-sm md:text-base text-foreground-950 font-body mt-0.5">{m.sub}</p>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}